import { useMemo } from 'react';
import { BuildingOffice2Icon, PlusIcon } from '@heroicons/react/24/outline';
import {
    DataTable,
    type DataTableFilter,
    type DataTableMode,
    type DataTablePagination,
    type DataTableRefs,
    type DataTableSearchConfig,
    type DataTableSort
} from '@odo/shared-ui';
import type { PracticeListItem, PracticesSortBy } from '../../../services/accounts';
import { buildPracticeColumns } from './practiceColumns';

const SKELETON_ROW_COUNT = 9;

function SkeletonCell({ width }: { width: string }) {
    return <div className={`h-3.5 rounded bg-gray-200 animate-pulse ${width}`} />;
}

function SkeletonRows() {
    const widths = ['w-44', 'w-28', 'w-8', 'w-24', 'w-14', 'w-20'];
    return (
        <>
            {Array.from({ length: SKELETON_ROW_COUNT }).map((_, i) => (
                <tr key={i} className="h-14">
                    {widths.map((width, j) => (
                        <td key={j} className="px-3 first:pl-5 align-middle">
                            <SkeletonCell width={width} />
                        </td>
                    ))}
                    <td className="px-3 last:pr-4 align-middle" />
                </tr>
            ))}
        </>
    );
}

interface EmptyStateProps {
    search: string;
    hasFilters: boolean;
    onClearFilters: () => void;
    onAddPractice: () => void;
}

// A filtered-empty result reads as "no match" (search text or generic
// filters message), only falling back to "Clear search" wording when no
// dropdown filter is active - mirrors CommunicationsTable's EmptyState.
function EmptyState({ search, hasFilters, onClearFilters, onAddPractice }: EmptyStateProps) {
    const hasSearch = search.length > 0;
    return (
        <div className="flex flex-col items-center gap-3 text-center">
            <BuildingOffice2Icon className="h-12 w-12 text-gray-300" strokeWidth={1} />
            <p className="text-sm text-gray-500">
                {hasSearch ? (
                    <>No practices match &ldquo;{search}&rdquo;.</>
                ) : (
                    'No practices match your filters.'
                )}
            </p>
            <div className="flex gap-2.5 mt-1">
                <button
                    type="button"
                    onClick={onClearFilters}
                    className="bg-white text-gray-800 border border-gray-300 px-4 py-2 rounded-lg hover:bg-gray-50 transition-colors font-semibold text-sm"
                >
                    {hasFilters ? 'Clear filters' : 'Clear search'}
                </button>
                <button
                    type="button"
                    onClick={onAddPractice}
                    className="bg-gray-800 text-white rounded-lg px-4 py-2 text-sm font-semibold hover:bg-gray-900 transition-colors"
                >
                    + Add practice
                </button>
            </div>
        </div>
    );
}

interface PracticesTableProps {
    practices: PracticeListItem[];
    isLoading: boolean;
    search: string;
    hasFilters: boolean;
    onClearFilters: () => void;
    onOpenPractice: (id: string) => void;
    onAddPractice: () => void;
    sort: DataTableSort<PracticesSortBy>;
    mode: DataTableMode;
    onModeChange: (mode: DataTableMode) => void;
    pagination: DataTablePagination;
    onLoadMore: () => void;
    hasMore: boolean;
    isLoadingMore: boolean;
    searchConfig: DataTableSearchConfig;
    filters: DataTableFilter[];
    refs?: DataTableRefs;
}

export function PracticesTable({
    practices,
    isLoading,
    search,
    hasFilters,
    onClearFilters,
    onOpenPractice,
    onAddPractice,
    sort,
    mode,
    onModeChange,
    pagination,
    onLoadMore,
    hasMore,
    isLoadingMore,
    searchConfig,
    filters,
    refs
}: PracticesTableProps) {
    const columns = useMemo(() => buildPracticeColumns({ onOpenPractice }), [onOpenPractice]);

    return (
        <DataTable
            label="Practices"
            columns={columns}
            rows={practices}
            getRowKey={(practice) => practice.id}
            rowAction={{ onActivate: (practice) => onOpenPractice(practice.id) }}
            rowHeightClassName="h-14"
            sort={sort}
            mode={mode}
            onModeChange={onModeChange}
            pagination={pagination}
            onLoadMore={onLoadMore}
            hasMore={hasMore}
            isLoadingMore={isLoadingMore}
            isLoading={isLoading}
            loadingRows={<SkeletonRows />}
            emptyState={
                <EmptyState
                    search={search}
                    hasFilters={hasFilters}
                    onClearFilters={onClearFilters}
                    onAddPractice={onAddPractice}
                />
            }
            emptyStateCellClassName="px-3 py-16"
            search={searchConfig}
            filters={filters}
            actions={[
                {
                    key: 'add-practice',
                    label: 'Add practice',
                    icon: <PlusIcon className="h-4 w-4" />,
                    variant: 'primary',
                    onClick: onAddPractice
                }
            ]}
            refs={refs}
            className="overflow-hidden px-8 pb-8"
        />
    );
}
